import { styled, Tab as MuiTab, Tabs } from '@mui/material'
import { IconContext } from '@phosphor-icons/react'
import { tokens } from 'ComponentLibrary'
import { ReactElement, ReactNode } from 'react'
import { Link as RouterLink } from 'react-router-dom'

const DEFAULT_TAB_HEIGHT = '40px'

type TabProps<T extends string | number> = {
    label: string
    icon?: ReactElement
    value: T
    customHeight?: string
    disabled?: boolean
}

type NavTabProps<T extends string | number> = {
    href: string
} & TabProps<T>

type TabBarProps<T extends string | number> = {
    handleChange?: (newValue: T) => void
    selectedTab?: T
    ariaLabel?: string
    type?: 'bunched' | 'stretched'
    children: ReactNode
}

export function TabBar<T extends string | number>({ selectedTab, type = 'bunched', ariaLabel, handleChange, children }: TabBarProps<T>) {
    return (
        <IconContext.Provider
            value={{
                size: tokens.SizingIconS,
                weight: 'bold',
            }}
        >
            <StyledTabs
                variant={type === 'stretched' ? 'fullWidth' : 'standard'}
                value={selectedTab}
                onChange={(_, newValue) => handleChange?.(newValue)}
                aria-label={ariaLabel}
            >
                {children}
            </StyledTabs>
        </IconContext.Provider>
    )
}

export function Tab<T extends string | number>({ label, icon, value, customHeight, disabled, ...props }: TabProps<T>) {
    return (
        <ContainerTab
            disabled={disabled}
            label={label}
            value={value}
            iconPosition="start"
            icon={icon}
            sx={{ height: customHeight ?? DEFAULT_TAB_HEIGHT, minHeight: customHeight ?? DEFAULT_TAB_HEIGHT }}
            {...props}
        />
    )
}

export function NavTab<T extends string | number>({ label, icon, value, href, customHeight, disabled, ...props }: NavTabProps<T>) {
    return (
        <ContainerTab
            disabled={disabled}
            label={label}
            value={value}
            iconPosition="start"
            icon={icon}
            sx={{ height: customHeight ?? DEFAULT_TAB_HEIGHT, minHeight: customHeight ?? DEFAULT_TAB_HEIGHT }}
            {...props}
            component={RouterLink}
            to={href}
        />
    )
}

const StyledTabs = styled(Tabs)(() => ({
    minHeight: 'unset',
    '& .MuiTabs-flexContainer': {
        gap: 0,
        borderBottom: `${tokens.SizingBorderM} solid ${tokens.ColorBorderDefault}`,
    },
    '& .MuiTabs-indicator': {
        height: tokens.SizingBorderM,
        backgroundColor: tokens.ColorBorderSelectedDefault,
    },
}))

const ContainerTab = styled(MuiTab)(() => ({
    minHeight: DEFAULT_TAB_HEIGHT,
    color: tokens.ColorTextDisabled,
    borderTopLeftRadius: tokens.SizingCornerS,
    borderTopRightRadius: tokens.SizingCornerS,
    '&:hover': {
        color: tokens.ColorTextLinkHover,
        backgroundColor: 'transparent',
        '& > .MuiTab-iconWrapper': {
            color: tokens.ColorIconLinkHover,
            fill: tokens.ColorIconLinkHover,
        },
    },
    '&:active': {
        color: tokens.ColorTextLinkPressed,
        '& > .MuiTab-iconWrapper': {
            color: tokens.ColorIconLinkPressed,
            fill: tokens.ColorIconLinkPressed,
        },
    },
    '&.Mui-selected': {
        color: tokens.ColorTextLinkSelected,
        backgroundColor: tokens.ColorSurfaceSelectedDefault,
    },
    '&.Mui-selected:hover': {
        color: tokens.ColorTextLinkHover,
        backgroundColor: tokens.ColorSurfaceSelectedHover,
        '& > .MuiTab-iconWrapper': {
            color: tokens.ColorIconLinkHover,
            fill: tokens.ColorIconLinkHover,
        },
    },
    '&.Mui-selected:active': {
        color: tokens.ColorTextLinkPressed,
        backgroundColor: tokens.ColorSurfaceSelectedPressed,
        '& > .MuiTab-iconWrapper': {
            color: tokens.ColorIconLinkPressed,
            fill: tokens.ColorIconLinkPressed,
        },
    },
    '& > .MuiTab-iconWrapper': {
        color: tokens.ColorIconDisabled,
        fill: tokens.ColorIconDisabled,
        marginRight: 0,
    },
    '&.Mui-selected > .MuiTab-iconWrapper': {
        color: tokens.ColorIconLinkSelected,
        fill: tokens.ColorIconLinkSelected,
    },
    ...tokens.Link,
    padding: `0 ${tokens.SizingSpacing300}`,
    gap: tokens.SizingSpacing075,
})) as typeof MuiTab
